import type { WorkspaceRetailKpiMetrics } from '@/utils/workspaceAdProductMetrics';
import { EMPTY_RETAIL_KPI_METRICS } from '@/utils/workspaceAdProductMetrics';
import {
  derivedForProduct,
  organicPaidFromSnapshot,
  tacosPercent,
  type OrganicPaidBuckets,
  type OrganicPaidPeriodView,
  type ProductTotals,
  type SnapshotDerived,
  type SnapshotPeriod,
} from '@/api/snapshotMetrics';

export const CONSOLIDATED_VIEW_QUERY = 'view';
export const CONSOLIDATED_VIEW_VALUE = 'consolidated';

export type MarketplaceFilter = 'all' | 'amazon' | 'flipkart';
export type PerformanceFilter = 'all' | 'paid' | 'organic';

export function isConsolidatedDashboardView(search: string): boolean {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw).get(CONSOLIDATED_VIEW_QUERY) === CONSOLIDATED_VIEW_VALUE;
}

export function isAmazonDashboardPath(pathname: string): boolean {
  return /^\/accounts\/[^/]+\/campaigns\/dashboard\/?$/.test(pathname);
}

/** Additive metrics for one paid or organic channel. Null means the source does not provide it. */
export type ChannelSlice = {
  sales: number | null;
  orders: number | null;
  gvs: number | null;
  clicks: number | null;
  spend: number | null;
};

export type MarketplacePair = {
  paid: ChannelSlice;
  organic: ChannelSlice;
};

export type SummaryFigures = {
  sales: number | null;
  orders: number | null;
  gvs: number | null;
  aov: number | null;
  cr: number | null;
  tacos: number | null;
  acos: number | null;
};

export type ConsolidatedAsinRow = {
  id: string;
  productName: string;
  amazon: MarketplacePair;
  flipkart: MarketplacePair;
  consolidated: MarketplacePair;
};

export type PaidReportTotals = {
  revenue: number;
  orders: number;
  clicks: number;
  spend: number;
};

const EMPTY_DERIVED: SnapshotDerived = {
  aov: 0,
  cr: 0,
  acos: 0,
  cpm: 0,
  rpc: 0,
  cpo: 0,
  troas: 0,
};

export function emptyChannelSlice(): ChannelSlice {
  return { sales: null, orders: null, gvs: null, clicks: null, spend: null };
}

export function emptyMarketplacePair(): MarketplacePair {
  return { paid: emptyChannelSlice(), organic: emptyChannelSlice() };
}

function addNullable(left: number | null, right: number | null): number | null {
  if (left == null && right == null) return null;
  return (left ?? 0) + (right ?? 0);
}

export function addChannelSlices(left: ChannelSlice, right: ChannelSlice): ChannelSlice {
  return {
    sales: addNullable(left.sales, right.sales),
    orders: addNullable(left.orders, right.orders),
    gvs: addNullable(left.gvs, right.gvs),
    clicks: addNullable(left.clicks, right.clicks),
    spend: addNullable(left.spend, right.spend),
  };
}

export function addMarketplacePairs(left: MarketplacePair, right: MarketplacePair): MarketplacePair {
  return {
    paid: addChannelSlices(left.paid, right.paid),
    organic: addChannelSlices(left.organic, right.organic),
  };
}

export type AmazonProfileCurrency = {
  profileId: string;
  currency: string | null;
};

export type AmazonCurrencyView = {
  currency: string | null;
  profileIds: string[];
  current: OrganicPaidPeriodView | null;
  previous: OrganicPaidPeriodView | null;
};

export type FlipkartAccountRef = {
  credentialId: string;
  accountId: string;
};

function addProductTotals(left: ProductTotals, right: ProductTotals): ProductTotals {
  return {
    impressions: left.impressions + right.impressions,
    clicks: left.clicks + right.clicks,
    cost: left.cost + right.cost,
    purchases30d: left.purchases30d + right.purchases30d,
    sales30d: left.sales30d + right.sales30d,
  };
}

const ZERO_PRODUCT: ProductTotals = {
  impressions: 0,
  clicks: 0,
  cost: 0,
  purchases30d: 0,
  sales30d: 0,
};

/** Sum Amazon dashboard views that share a currency. Rates are recomputed from the totals. */
export function sumOrganicPaidViews(views: OrganicPaidPeriodView[]): OrganicPaidPeriodView | null {
  if (views.length === 0) return null;
  const organic = views.reduce(
    (total, view) => addProductTotals(total, view.buckets.organic),
    { ...ZERO_PRODUCT }
  );
  const paid = views.reduce(
    (total, view) => addProductTotals(total, view.buckets.paid),
    { ...ZERO_PRODUCT }
  );
  const salesTotal = views.reduce((total, view) => total + view.salesTotal, 0);
  const ordersTotal = views.reduce((total, view) => total + view.ordersTotal, 0);
  const gvTotal = views.reduce((total, view) => total + view.gvTotal, 0);
  const spendKnown = views.some((view) => view.taosSpend != null);
  const taosSpend = spendKnown
    ? views.reduce((total, view) => total + (view.taosSpend ?? 0), 0)
    : null;
  return {
    buckets: { organic, paid },
    salesTotal,
    ordersTotal,
    gvTotal,
    aov: ordersTotal > 0 ? salesTotal / ordersTotal : 0,
    cr: gvTotal > 0 ? (ordersTotal / gvTotal) * 100 : 0,
    taos: taosSpend != null && taosSpend > 0 ? salesTotal / taosSpend : 0,
    acos: paid.sales30d > 0 ? (paid.cost / paid.sales30d) * 100 : 0,
    taosSpend,
    adsExceedRetail: views.some((view) => view.adsExceedRetail),
    attributedAdSales: views.reduce((total, view) => total + view.attributedAdSales, 0),
    adsOrdersExceedRetail: views.some((view) => view.adsOrdersExceedRetail),
    attributedAdOrders: views.reduce((total, view) => total + view.attributedAdOrders, 0),
  };
}

/** Missing currency stays in its own group so it is not added to a known currency. */
export function groupProfileIdsByCurrency(
  profiles: AmazonProfileCurrency[]
): Array<{ currency: string | null; profileIds: string[] }> {
  const order: string[] = [];
  const groups = new Map<string, string[]>();
  for (const profile of profiles) {
    const currency = (profile.currency || '').trim().toUpperCase();
    if (!groups.has(currency)) {
      groups.set(currency, []);
      order.push(currency);
    }
    groups.get(currency)?.push(profile.profileId);
  }
  return order.map((currency) => ({
    currency: currency || null,
    profileIds: groups.get(currency) ?? [],
  }));
}

export function flipkartAccountKey(account: FlipkartAccountRef): string {
  return `${account.credentialId}:${account.accountId}`;
}

/** `mapped === null` keeps every account (single-profile consolidated view). */
export function filterFlipkartAccountsForBrand<T extends FlipkartAccountRef>(
  accounts: T[],
  mapped: readonly FlipkartAccountRef[] | null
): T[] {
  if (mapped == null) return accounts;
  const keys = new Set(mapped.map(flipkartAccountKey));
  return accounts.filter((account) => keys.has(flipkartAccountKey(account)));
}

export function parseFlipkartAdsExternalId(externalId: string): FlipkartAccountRef | null {
  const index = externalId.indexOf(':');
  if (index <= 0 || index >= externalId.length - 1) return null;
  return {
    credentialId: externalId.slice(0, index),
    accountId: externalId.slice(index + 1),
  };
}

export function sliceTotal(pair: MarketplacePair, key: 'sales' | 'orders' | 'gvs'): number | null {
  return addNullable(pair.paid[key], pair.organic[key]);
}

export function channelHasValue(slice: ChannelSlice): boolean {
  return (
    slice.sales != null ||
    slice.orders != null ||
    slice.gvs != null ||
    slice.clicks != null ||
    slice.spend != null
  );
}

export function pairHasValue(pair: MarketplacePair): boolean {
  return channelHasValue(pair.paid) || channelHasValue(pair.organic);
}

export function pairFromOrganicPaidView(view: OrganicPaidPeriodView): MarketplacePair {
  const paid = view.buckets.paid;
  const organic = view.buckets.organic;
  return {
    paid: {
      sales: paid.sales30d,
      orders: paid.purchases30d,
      gvs: paid.clicks,
      clicks: paid.clicks,
      spend: paid.cost,
    },
    organic: {
      sales: organic.sales30d,
      orders: organic.purchases30d,
      gvs: organic.clicks,
      clicks: organic.clicks,
      spend: organic.cost,
    },
  };
}

function periodFromAd(ad: {
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
}): SnapshotPeriod {
  return {
    range: {},
    totals: {
      impressions: 0,
      clicks: ad.clicks,
      cost: ad.cost,
      purchases30d: ad.purchases30d,
      sales30d: ad.sales30d,
      ctr: 0,
      cpc: 0,
      roas: 0,
    },
    derived: { ...EMPTY_DERIVED },
    byAdProduct: {},
    rowCount: 1,
  };
}

/** Per-ASIN Amazon split. Uses the same resolver as the dashboard gauges. */
export function amazonAsinPair(
  ad: { clicks: number; cost: number; purchases30d: number; sales30d: number } | null,
  retail: WorkspaceRetailKpiMetrics = EMPTY_RETAIL_KPI_METRICS
): MarketplacePair {
  const metrics = ad ?? { clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 };
  return pairFromOrganicPaidView(
    organicPaidFromSnapshot(periodFromAd(metrics), retail, { demo: false })
  );
}

/**
 * Flipkart product reports are paid ads. Organic retail is not in those responses,
 * so the organic channel stays empty instead of being reported as zero.
 * Paid glance views use ad clicks, matching the paid half of the dashboard GVs card.
 */
export function flipkartPaidPair(metrics: PaidReportTotals): MarketplacePair {
  return {
    paid: {
      sales: metrics.revenue,
      orders: metrics.orders,
      gvs: metrics.clicks,
      clicks: metrics.clicks,
      spend: metrics.spend,
    },
    organic: emptyChannelSlice(),
  };
}

export function applyPerformance(
  pair: MarketplacePair,
  performance: PerformanceFilter
): MarketplacePair {
  if (performance === 'paid') return { paid: pair.paid, organic: emptyChannelSlice() };
  if (performance === 'organic') return { paid: emptyChannelSlice(), organic: pair.organic };
  return pair;
}

export function selectSummaryPair(
  amazon: MarketplacePair,
  flipkart: MarketplacePair,
  consolidated: MarketplacePair,
  marketplace: MarketplaceFilter
): MarketplacePair {
  if (marketplace === 'amazon') return amazon;
  if (marketplace === 'flipkart') return flipkart;
  return consolidated;
}

export function bucketsFromPair(pair: MarketplacePair): OrganicPaidBuckets {
  const toProduct = (slice: ChannelSlice): ProductTotals => ({
    impressions: 0,
    clicks: slice.gvs ?? 0,
    cost: slice.spend ?? 0,
    purchases30d: slice.orders ?? 0,
    sales30d: slice.sales ?? 0,
  });
  return { organic: toProduct(pair.organic), paid: toProduct(pair.paid) };
}

/** TACOS needs known organic sales. Unknown organic stays blank rather than assumed zero. */
export function pairTacos(pair: MarketplacePair): number | null {
  const spend = pair.paid.spend;
  if (spend == null || pair.paid.sales == null || pair.organic.sales == null) return null;
  return tacosPercent(spend, bucketsFromPair(pair));
}

export function pairAcos(pair: MarketplacePair): number | null {
  const spend = pair.paid.spend;
  const sales = pair.paid.sales;
  if (spend == null || sales == null) return null;
  return sales > 0 ? (spend / sales) * 100 : 0;
}

export function channelAov(slice: ChannelSlice): number | null {
  if (slice.sales == null || slice.orders == null) return null;
  return derivedForProduct({
    impressions: 0,
    clicks: slice.clicks ?? 0,
    cost: slice.spend ?? 0,
    purchases30d: slice.orders,
    sales30d: slice.sales,
  }).aov;
}

export function channelCr(slice: ChannelSlice): number | null {
  if (slice.orders == null || slice.clicks == null) return null;
  return derivedForProduct({
    impressions: 0,
    clicks: slice.clicks,
    cost: slice.spend ?? 0,
    purchases30d: slice.orders,
    sales30d: slice.sales ?? 0,
  }).cr;
}

/** ACOS is an ad rate. A zero-spend channel (organic) has no ACOS. */
export function channelAcos(slice: ChannelSlice): number | null {
  if (slice.spend == null || slice.sales == null || slice.spend === 0) return null;
  return slice.sales > 0 ? (slice.spend / slice.sales) * 100 : 0;
}

export function figuresFromPair(pair: MarketplacePair): SummaryFigures {
  const sales = sliceTotal(pair, 'sales');
  const orders = sliceTotal(pair, 'orders');
  const gvs = sliceTotal(pair, 'gvs');
  const aov = sales != null && orders != null ? (orders > 0 ? sales / orders : 0) : null;
  const cr = orders != null && gvs != null ? (gvs > 0 ? (orders / gvs) * 100 : 0) : null;
  return {
    sales,
    orders,
    gvs,
    aov,
    cr,
    tacos: pairTacos(pair),
    acos: pairAcos(pair),
  };
}

/** Amazon-only, unfiltered cards use the dashboard view fields directly. */
export function figuresFromAmazonView(view: OrganicPaidPeriodView): SummaryFigures {
  return {
    sales: view.salesTotal,
    orders: view.ordersTotal,
    gvs: view.gvTotal,
    aov: view.aov,
    cr: view.cr,
    tacos: tacosPercent(view.taosSpend, view.buckets),
    acos: view.acos,
  };
}

export function summaryFigures(args: {
  amazonView: OrganicPaidPeriodView | null;
  amazonPair: MarketplacePair;
  flipkartPair: MarketplacePair;
  consolidatedPair: MarketplacePair;
  marketplace: MarketplaceFilter;
  performance: PerformanceFilter;
}): SummaryFigures {
  const flipkartMissing = !pairHasValue(args.flipkartPair);
  if (
    args.performance === 'all' &&
    args.amazonView &&
    (args.marketplace === 'amazon' || (args.marketplace === 'all' && flipkartMissing))
  ) {
    return figuresFromAmazonView(args.amazonView);
  }
  return figuresFromPair(
    applyPerformance(
      selectSummaryPair(args.amazonPair, args.flipkartPair, args.consolidatedPair, args.marketplace),
      args.performance
    )
  );
}

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

export function buildConsolidatedAsinRows(args: {
  amazon: Array<{
    asin: string;
    productName: string;
    sku?: string | null;
    ad: { clicks: number; cost: number; purchases30d: number; sales30d: number } | null;
    retail: WorkspaceRetailKpiMetrics;
  }>;
  flipkart: Array<{
    id: string;
    productName: string | null;
    aliases: string[];
    metrics: PaidReportTotals;
  }>;
}): ConsolidatedAsinRow[] {
  const rows: ConsolidatedAsinRow[] = [];
  const byAsin = new Map<string, ConsolidatedAsinRow>();
  const bySku = new Map<string, ConsolidatedAsinRow>();

  for (const item of args.amazon) {
    const id = item.asin.trim();
    const key = norm(id);
    if (!key) continue;
    const amazon = amazonAsinPair(item.ad, item.retail);
    const row: ConsolidatedAsinRow = {
      id,
      productName: item.productName.trim() || id,
      amazon,
      flipkart: emptyMarketplacePair(),
      consolidated: amazon,
    };
    rows.push(row);
    byAsin.set(key, row);
    const sku = norm(item.sku);
    if (sku && sku !== key && !bySku.has(sku)) bySku.set(sku, row);
  }

  for (const product of args.flipkart) {
    const aliases = product.aliases.map(norm).filter(Boolean);
    let match: ConsolidatedAsinRow | undefined;
    for (const alias of aliases) {
      match = byAsin.get(alias);
      if (match) break;
    }
    if (!match) {
      for (const alias of aliases) {
        match = bySku.get(alias);
        if (match) break;
      }
    }
    const flipkart = flipkartPaidPair(product.metrics);
    if (match) {
      match.flipkart = addMarketplacePairs(match.flipkart, flipkart);
      if (product.productName && (match.productName === match.id || !match.productName)) {
        match.productName = product.productName;
      }
      continue;
    }
    const id = (product.id || aliases[0] || '').trim();
    if (!id) continue;
    const row: ConsolidatedAsinRow = {
      id,
      productName: product.productName?.trim() || id,
      amazon: emptyMarketplacePair(),
      flipkart,
      consolidated: flipkart,
    };
    rows.push(row);
    const key = norm(id);
    if (key) byAsin.set(key, row);
  }

  for (const row of rows) {
    row.consolidated = addMarketplacePairs(row.amazon, row.flipkart);
  }

  rows.sort((left, right) => {
    const leftSales = sliceTotal(left.consolidated, 'sales') ?? -1;
    const rightSales = sliceTotal(right.consolidated, 'sales') ?? -1;
    return rightSales - leftSales || left.id.localeCompare(right.id);
  });
  return rows;
}

export function rowMatchesMarketplace(row: ConsolidatedAsinRow, marketplace: MarketplaceFilter): boolean {
  if (marketplace === 'amazon') return pairHasValue(row.amazon);
  if (marketplace === 'flipkart') return pairHasValue(row.flipkart);
  return pairHasValue(row.amazon) || pairHasValue(row.flipkart);
}
